/* global MonerisApplePay, ApplePaySession */
import React, { useEffect, useRef, useState, useCallback } from "react";
import styled from "styled-components";
import { createApplePaySession } from "../utilities/axios";

const ApplePayButtonContainer = styled.div``;

const ApplePayButtonWrap = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};

  apple-pay-button {
    width: 100%;
    height: 45px;
    border-radius: 12px;
  }
`;
const DisabledOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  cursor: not-allowed;
`;

const ApplePayButton = ({
  amount,
  paymentGuid,
  onPaymentSuccess,
  onPaymentError,
  disabled = false,
}) => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  void isProcessing;
  const applePaySessionRef = useRef(null);
  const applePayButtonRef = useRef(null);
  const isProcessingRef = useRef(false);
  const dbg = useCallback((msg) => {
    console.log("[ApplePay]", msg);
  }, []);

  // Load Apple Pay SDK and check availability
  useEffect(() => {
    const existing = document.querySelector(
      'script[src*="applepay.cdn-apple.com"]',
    );
    if (existing) {
      setIsAvailable(!!window.ApplePaySession);
      return;
    }
    const script = document.createElement("script");
    script.src =
      "https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js";
    script.async = true;
    script.onload = () => setIsAvailable(!!window.ApplePaySession);
    script.onerror = () =>
      console.error("[ApplePay] Failed to load Apple Pay SDK");
    document.head.appendChild(script);
  }, []);

  const loadMonerisApplePay = useCallback((apiDomain) => {
    const scriptUrl = `https://${apiDomain}/applepayjs/applepay-api.js`;
    const existing = document.querySelector(`script[src="${scriptUrl}"]`);
    if (existing) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = scriptUrl;
      script.async = true;
      script.onload = () => {
        if (window.MonerisApplePay) {
          try {
            MonerisApplePay.onReady();
          } catch (err) {
            console.error("[ApplePay] MonerisApplePay.onReady error:", err);
          }
        }
        resolve();
      };
      script.onerror = () => {
        reject(new Error("Moneris Apple Pay script failed to load"));
      };
      document.body.appendChild(script);
    });
  }, []);

  const setupMonerisContainer = useCallback(
    (storeId, merchantIdentifier, displayName) => {
      let elem = document.getElementById("moneris-apple-pay");
      if (!elem) {
        elem = document.createElement("div");
        elem.style.cssText = "display:none";
        elem.id = "moneris-apple-pay";
        document.body.appendChild(elem);
      }
      elem.setAttribute("store-id", storeId);
      elem.setAttribute("merchant-identifier", merchantIdentifier);
      elem.setAttribute("display-name", displayName);
    },
    [],
  );

  const handleApplePayClick = useCallback(() => {
    if (
      isProcessingRef.current ||
      disabled ||
      !window.ApplePaySession ||
      !amount ||
      !paymentGuid
    ) {
      dbg(
        `BLOCKED: processing=${isProcessingRef.current} disabled=${disabled} ApplePaySession=${!!window.ApplePaySession} amount=${amount} guid=${paymentGuid}`,
      );
      return;
    }

    isProcessingRef.current = true;
    setIsProcessing(true);

    try {
      let sessionVersion = 12;
      while (
        sessionVersion > 0 &&
        !ApplePaySession.supportsVersion(sessionVersion)
      ) {
        sessionVersion--;
      }

      if (sessionVersion <= 0) {
        dbg("FAILED: ApplePaySession not supported on this device");
        onPaymentError?.({
          message: "Apple Pay is not supported on this device.",
        });
        isProcessingRef.current = false;
        setIsProcessing(false);
        return;
      }

      const paymentRequest = {
        currencyCode: "USD",
        countryCode: "US",
        total: {
          label: "PhoneBox Service",
          type: "final",
          amount: String(amount),
        },
        merchantCapabilities: [
          "supports3DS",
          "supportsEMV",
          "supportsCredit",
          "supportsDebit",
        ],
        supportedNetworks: ["amex", "masterCard", "visa"],
      };

      const session = new ApplePaySession(sessionVersion, paymentRequest);
      applePaySessionRef.current = session;

      dbg(`ApplePaySession v${sessionVersion} created`);

      session.onvalidatemerchant = async (event) => {
        dbg(`onvalidatemerchant: ${event.validationURL}`);

        try {
          const sessionResponse = await createApplePaySession(paymentGuid);

          const rawData = sessionResponse.data;
          const sessionContent = rawData?.data || rawData?.content || rawData;

          const sessionIsSuccess =
            sessionContent?.isSuccess ?? sessionContent?.IsSuccess;
          const sessionTicket =
            sessionContent?.preloadTicket || sessionContent?.PreloadTicket;
          const sessionOrderId =
            sessionContent?.orderId || sessionContent?.OrderId;
          const sessionApiDomain =
            sessionContent?.monerisApiDomain ||
            sessionContent?.MonerisApiDomain ||
            "www3.moneris.com";
          const sessionStoreId =
            sessionContent?.monerisStoreId || sessionContent?.MonerisStoreId;
          const sessionMerchantId =
            sessionContent?.monerisMerchantIdentifier ||
            sessionContent?.MonerisMerchantIdentifier;
          const sessionAmount =
            sessionContent?.amount || sessionContent?.Amount;
          const sessionErrorMessage =
            sessionContent?.errorMessage ||
            sessionContent?.ErrorMessage ||
            sessionContent?.message ||
            sessionContent?.Message;
          dbg(
            `isSuccess=${sessionIsSuccess} hasTicket=${!!sessionTicket} orderId=${sessionOrderId}`,
          );

          if (!sessionIsSuccess || !sessionTicket) {
            const backendMessage =
              sessionErrorMessage || "Failed to create Apple Pay session";

            dbg(`FAILED: ${backendMessage}`);
            session.abort();
            throw new Error(backendMessage);
          }

          setupMonerisContainer(
            sessionStoreId,
            sessionMerchantId,
            `PhoneBox-${sessionOrderId}`,
          );

          await loadMonerisApplePay(sessionApiDomain);

          if (!window.MonerisApplePay) {
            session.abort();
            throw new Error("Moneris not ready");
          }

          MonerisApplePay.ticket = sessionTicket;

          window.MonerisApplePay.validateSession(
            event.validationURL,
            (response) => {
              dbg("Merchant validated");
              session.completeMerchantValidation(response);
            },
            (err) => {
              dbg(`Merchant validation FAILED: ${err}`);
              session.abort();
              isProcessingRef.current = false;
              setIsProcessing(false);
              onPaymentError?.({
                message: "Merchant validation failed",
              });
            },
          );

          session._pb_amount = sessionAmount;
          session._pb_orderId = sessionOrderId;
        } catch (err) {
          const backendMessage =
            err?.response?.data?.errorMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Apple Pay failed";

          dbg(`ERROR(validate): ${backendMessage}`);
          session.abort();
          applePaySessionRef.current = null;
          isProcessingRef.current = false;
          setIsProcessing(false);
          onPaymentError?.({ message: backendMessage });
        }
      };

      session.onpaymentauthorized = (event) => {
        dbg("onpaymentauthorized");

        const paymentData = {
          orderId: session._pb_orderId,
          amount: session._pb_amount,
          payment: event.payment,
        };

        window.MonerisApplePay.purchase(paymentData, (response) => {
          const code = response?.receipt?.ResponseCode;
          const responseCode =
            code != null && !isNaN(code) ? parseInt(code, 10) : null;

          if (responseCode !== null && responseCode < 50) {
            session.completePayment(ApplePaySession.STATUS_SUCCESS);
            dbg("Payment SUCCESS");
            onPaymentSuccess?.(response);
            return;
          }

          session.completePayment(ApplePaySession.STATUS_FAILURE);
          dbg("Payment DECLINED");

          isProcessingRef.current = false;
          setIsProcessing(false);

          onPaymentError?.({
            message: response?.receipt?.Message || "Payment declined",
          });
        });
      };

      session.oncancel = () => {
        dbg("Session cancelled");
        applePaySessionRef.current = null;
        isProcessingRef.current = false;
        setIsProcessing(false);
      };

      session.begin();
    } catch (error) {
      dbg(`ERROR: ${error.message}`);
      isProcessingRef.current = false;
      setIsProcessing(false);
      onPaymentError?.({ message: error.message });
    }
  }, [
    disabled,
    amount,
    paymentGuid,
    onPaymentSuccess,
    onPaymentError,
    setupMonerisContainer,
    loadMonerisApplePay,
    dbg,
  ]);

  // Attach click listener to the web component (React onClick doesn't work on custom elements)
  useEffect(() => {
    const btn = applePayButtonRef.current;
    if (!btn) return;
    btn.addEventListener("click", handleApplePayClick);
    return () => btn.removeEventListener("click", handleApplePayClick);
  }, [handleApplePayClick]);

  if (!isAvailable) return null;

  return (
    <ApplePayButtonContainer>
      <ApplePayButtonWrap $disabled={disabled || isProcessing}>
        {(disabled || isProcessing) && <DisabledOverlay />}
        <apple-pay-button
          ref={applePayButtonRef}
          buttonstyle="black"
          type="plain"
          locale="en"
          style={{
            width: "100%",
            height: "45px",
            borderRadius: "12px",
            display: "block",
            "--apple-pay-button-width": "100%",
            "--apple-pay-button-height": "45px",
            "--apple-pay-button-border-radius": "12px",
          }}
        />
      </ApplePayButtonWrap>
    </ApplePayButtonContainer>
  );
};

export default ApplePayButton;
